/**
 * Browser-side push opt-in.
 *
 * Rules:
 *  • Firebase Cloud Messaging is the primary transport. Legacy VAPID Web Push
 *    is kept only as a fallback for browsers/hosts where Firebase cannot
 *    initialise, so existing subscribers keep working during the migration.
 *  • Never prompt in dev, inside an iframe, or on Lovable preview hosts —
 *    permission requests from iframes are blocked and would burn the user's
 *    one-shot decision.
 *  • Never prompt cold: the caller decides the moment (explicit opt-in tap).
 *    A refused/denied decision is remembered locally so we never re-ask.
 *  • The messaging workers are registered on their own scopes and never touch
 *    the PWA app-shell worker.
 */
import { getAnonymousId } from "@/lib/identity/anonymousId";
import { getPushPublicKey, savePushSubscription, removePushSubscription } from "./push.functions";
import { deleteFcmRegistration, getFcmRegistrationToken, logPushIssue } from "./fcm-client";
import { isFcmConfigured } from "./fcm-config";

const DISMISS_KEY = "truwe_push_dismissed_visit";
const ASKED_KEY = "truwe_push_asked";
const SW_URL = "/push-sw.js";
const SW_SCOPE = "/push-sw-scope/";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function bufToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function inBlockedContext(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host.endsWith(".lovableproject.com") || host.endsWith(".lovableproject-dev.com")) return true;
  if (host.endsWith(".beta.lovable.dev")) return true;
  return false;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** True when we may show our own soft prompt for this visitor. */
export function canShowSoftPrompt(): boolean {
  if (!isPushSupported() || inBlockedContext()) return false;
  if (Notification.permission !== "default") return false;
  try {
    if (localStorage.getItem(ASKED_KEY) === "1") return false;
    // "Not now" silences the card for this visit only — it re-asks next visit.
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return false;
  } catch {
    /* storage blocked — allow */
  }
  return true;
}

export function rememberSoftPromptDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

type EnableHooks = {
  onPermissionGranted?: () => void;
  onFailure?: (reason: string, detail?: string) => void;
};

export type EnablePushResult = "subscribed" | "denied" | "unsupported" | "error";

/**
 * Requests permission (only from an explicit user opt-in) and registers the
 * device. Returns the outcome so callers can adjust copy — never throws.
 */
export async function enablePush(
  promptSource: string,
  hooks?: EnableHooks,
): Promise<EnablePushResult> {
  if (!isPushSupported()) {
    hooks?.onFailure?.("unsupported_browser");
    return "unsupported";
  }
  if (inBlockedContext()) {
    // Preview iframes / localhost: never burn the permission decision.
    hooks?.onFailure?.("blocked_context");
    return "unsupported";
  }

  try {
    localStorage.setItem(ASKED_KEY, "1");
  } catch {
    /* ignore */
  }

  if (isFcmConfigured()) {
    const result = await enableViaFcm(promptSource, hooks);
    if (result !== "error") return result;
    // Firebase could not mint a token — fall through to legacy Web Push so the
    // visitor still gets notifications instead of nothing.
    logPushIssue("fcm_fallback_to_webpush", promptSource);
  }

  return enableViaWebPush(promptSource, hooks);
}

async function enableViaFcm(
  promptSource: string,
  hooks?: EnableHooks,
): Promise<EnablePushResult> {
  const token = await getFcmRegistrationToken(true);
  if (!token.ok) {
    if (token.reason === "permission_denied") return "denied";
    hooks?.onFailure?.(token.reason, token.detail);
    return token.reason === "unsupported_browser" ? "unsupported" : "error";
  }
  hooks?.onPermissionGranted?.();

  try {
    await savePushSubscription({
      data: {
        provider: "fcm",
        fcmToken: token.token,
        endpoint: `fcm:${token.token}`,
        anonymousId: getAnonymousId() || undefined,
        promptSource,
        userAgent: navigator.userAgent.slice(0, 400),
      },
    });
    return "subscribed";
  } catch (err) {
    logPushIssue("fcm_token_save_failed", err);
    hooks?.onFailure?.("token_save_failed", err instanceof Error ? err.message : String(err));
    return "error";
  }
}

/** Legacy VAPID Web Push — compatibility path only. */
async function enableViaWebPush(
  promptSource: string,
  hooks?: EnableHooks,
): Promise<EnablePushResult> {
  try {
    const permission =
      Notification.permission === "default"
        ? await Notification.requestPermission()
        : Notification.permission;
    if (permission !== "granted") {
      logPushIssue("permission_denied", permission);
      return "denied";
    }
    hooks?.onPermissionGranted?.();

    const { publicKey } = await getPushPublicKey();
    if (!publicKey) {
      logPushIssue("missing_vapid_key");
      hooks?.onFailure?.("missing_vapid_key");
      return "error";
    }

    const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    await navigator.serviceWorker.ready.catch(() => undefined);

    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ||
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      }));

    await savePushSubscription({
      data: {
        provider: "webpush",
        endpoint: sub.endpoint,
        p256dh: bufToBase64Url(sub.getKey("p256dh")),
        auth: bufToBase64Url(sub.getKey("auth")),
        anonymousId: getAnonymousId() || undefined,
        promptSource,
        userAgent: navigator.userAgent.slice(0, 400),
      },
    });
    return "subscribed";
  } catch (err) {
    logPushIssue("webpush_subscribe_failed", err);
    hooks?.onFailure?.("subscribe_failed", err instanceof Error ? err.message : String(err));
    return "error";
  }
}

/**
 * Silent re-registration for browsers that already granted permission.
 *
 * Never prompts (permission must already be "granted"), so it is safe to run
 * on every page load. This repairs devices whose opt-in was granted but whose
 * device token was never stored, so they show up in Admin → Messaging.
 */
export async function syncPushSubscriptionIfGranted(): Promise<boolean> {
  if (!isPushSupported() || inBlockedContext()) return false;
  if (Notification.permission !== "granted") return false;

  if (isFcmConfigured()) {
    const token = await getFcmRegistrationToken(false);
    if (!token.ok) reportResyncFailure(token.reason, token.detail);
    if (token.ok) {
      try {
        await savePushSubscription({
          data: {
            provider: "fcm",
            fcmToken: token.token,
            endpoint: `fcm:${token.token}`,
            anonymousId: getAnonymousId() || undefined,
            promptSource: "resync",
            userAgent: navigator.userAgent.slice(0, 400),
          },
        });
        return true;
      } catch (err) {
        logPushIssue("fcm_resync_save_failed", err);
        reportResyncFailure("token_save_failed", err instanceof Error ? err.message : String(err));
      }
    }
  }

  // Legacy path: reuse an existing Web Push subscription if one is present.
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return false;
    await savePushSubscription({
      data: {
        provider: "webpush",
        endpoint: sub.endpoint,
        p256dh: bufToBase64Url(sub.getKey("p256dh")),
        auth: bufToBase64Url(sub.getKey("auth")),
        anonymousId: getAnonymousId() || undefined,
        promptSource: "resync",
        userAgent: navigator.userAgent.slice(0, 400),
      },
    });
    return true;
  } catch (err) {
    logPushIssue("webpush_resync_failed", err);
    reportResyncFailure("resync_failed", err instanceof Error ? err.message : String(err));
    return false;
  }
}

/**
 * Resync used to fail silently in the console only, which is exactly why
 * "permission granted but zero stored devices" was invisible. Every failure
 * now lands in the same first-party ledger the admin dashboard reads.
 */
function reportResyncFailure(reason: string, detail?: string): void {
  logPushIssue(`resync:${reason}`, detail);
  void import("./telemetry")
    .then((m) => m.trackPushPrompt("error", { source: "resync", reason }))
    .catch(() => undefined);
}

/**
 * Identifier of this browser's stored subscription, if it has one. Used by the
 * admin panel to register the current browser as the test device.
 */
export async function getCurrentPushEndpoint(): Promise<string | null> {
  if (!isPushSupported() || Notification.permission !== "granted") return null;
  if (isFcmConfigured()) {
    const token = await getFcmRegistrationToken(false);
    if (token.ok) return `fcm:${token.token}`;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const sub = await reg?.pushManager.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
}

/** Unsubscribe this browser (used by an account-level toggle). */
export async function disablePush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  let ok = true;

  // Firebase registration
  try {
    const token = await getFcmRegistrationToken(false);
    if (token.ok) await removePushSubscription({ data: { endpoint: `fcm:${token.token}` } });
    await deleteFcmRegistration();
  } catch (err) {
    logPushIssue("fcm_unsubscribe_failed", err);
    ok = false;
  }

  // Legacy Web Push registration
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await removePushSubscription({ data: { endpoint: sub.endpoint } });
      await sub.unsubscribe();
    }
  } catch (err) {
    logPushIssue("webpush_unsubscribe_failed", err);
    ok = false;
  }

  return ok;
}
